import { FaSearch, FaFilter, FaSyncAlt, FaUserCog } from "react-icons/fa";
import { useState, useEffect, useRef } from 'react';

/**
 * COMPOSANT RECHERCHE ET FILTRES COMPACT ET SILENCIEUX
 */
const SearchAndFilters = ({ 
  searchTerm, 
  setSearchTerm, 
  handleCashFilter, 
  cashFilter, 
  handleAucFilter, 
  aucFilter, 
  resetFilters, 
  handleAccountTypeFilter, 
  accountTypeFilter,
  handleStatusFilter,
  statusFilter,
  handleManagedAccountFilter,
  isManagedAccountFilter,
  isSearching = false ,// Prop pour indiquer si une recherche est en cours
  onRefresh
}) => {
  const [localSearch, setLocalSearch] = useState(searchTerm || '');
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Debounce pour la recherche
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (localSearch !== searchTerm) {
        setSearchTerm(localSearch);
      }
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localSearch, searchTerm, setSearchTerm]);

  // Synchroniser avec les props externes
  useEffect(() => {
    if (searchTerm !== localSearch) {
      setLocalSearch(searchTerm || '');
    }
  }, [searchTerm]);

  const handleClearSearch = () => {
    setLocalSearch('');
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Compter les filtres actifs
  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (accountTypeFilter) count++;
    if (statusFilter) count++;
    if (isManagedAccountFilter) count++;
    if (cashFilter) count++;
    if (aucFilter) count++;
    return count;
  };

  const activeFilters = getActiveFiltersCount();

  return (
    <div className="search-filter-container mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        
        {/* Titre */}
        <div className="d-flex align-items-center gap-2">
          <FaSearch className="text-primary" size={20} />
          <h6 className="fw-bold mb-0">Comptes</h6>
        </div>
        
        {/* Contrôles de filtrage */}
        <div className="filter-controls d-flex flex-wrap align-items-center gap-2">
          
          {/* Recherche */}
          <div className="position-relative">
            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={16} />
            <input 
              ref={searchInputRef}
              type="text" 
              className="search-input ps-4" 
              placeholder="Rechercher..." 
              value={localSearch} 
              onChange={e => setLocalSearch(e.target.value)}
              style={{ 
                height: '38px',
                outline: 'none',
                boxShadow: 'none',
                borderColor: localSearch ? '#0d6efd' : '#dee2e6',
                width: '380px'
              }}
            />
            {localSearch && (
              <button
                className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0"
                onClick={handleClearSearch}
                type="button"
                style={{ 
                  color: '#6c757d',
                  outline: 'none',
                  boxShadow: 'none'
                }}
              >
                <i className="fas fa-times" style={{ fontSize: '14px' }}></i>
              </button>
            )}
            {/* Spinner discret pendant la recherche */}
            {isSearching && (
              <div 
                className="position-absolute top-50 end-0 translate-middle-y me-2"
                style={{ right: localSearch ? '28px' : '8px' }}
              >
                <div className="spinner-border spinner-border-sm text-muted" role="status" style={{ width: '12px', height: '12px' }}>
                  <span className="visually-hidden">Recherche...</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Filtre Administré */}
          <div className="position-relative">
            <select 
              className="account-type-select" 
              onChange={(e) => handleManagedAccountFilter(e.target.value)} 
              value={isManagedAccountFilter || ""}
              style={{ 
                height: '38px',
                width: '150px'
              }}
            >
              <option value="">Administré</option>
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </select>
          </div>
          
          {/* Filtre Statut */}
          <div className="position-relative">
            <select 
              className="account-type-select" 
              onChange={(e) => handleStatusFilter(e.target.value)} 
              value={statusFilter || ""}
              style={{ 
                height: '38px',
                width: '130px'
              }}
            >
              <option value="">Statut</option>
              <option value="0">Brouillon</option>
              <option value="1">En attente</option>
              <option value="2">Approuvé</option>
              <option value="3">Rejeté</option>
            </select>
          </div>
          
          {/* Tri Cash */}
          <button 
            className="filter-button d-flex align-items-center gap-1"
            onClick={handleCashFilter}
            style={{ 
              height: '38px', 
              padding: '0 12px',
              outline: 'none',
              minWidth: '80px'
            }}
          >
            Cash {cashFilter === 'asc' ? '▲' : cashFilter === 'desc' ? '▼' : ''}
          </button>
          
          {/* Tri AUC */}
          <button 
            className="filter-button d-flex align-items-center gap-1"
            onClick={handleAucFilter}
            style={{ 
              height: '38px', 
              padding: '0 12px',
              outline: 'none',
              minWidth: '80px'
            }}
          >
            AUC {aucFilter === 'asc' ? '▲' : aucFilter === 'desc' ? '▼' : ''}
          </button>
          
          {/* Filtre Type de compte */}
          <div className="position-relative">
            <select 
              className="account-type-select" 
              onChange={(e) => handleAccountTypeFilter(e.target.value)} 
              value={accountTypeFilter || ""}
              style={{ 
                height: '38px',
                width: '130px'
              }}
            >
              <option value="">Type</option>
              <option value="Particulier">Particulier</option>
              <option value="Entreprise">Entreprise</option>
              <option value="Association">Association</option>
              <option value="Banques">Banques</option>
              <option value="Assurance">Assurance</option>
              <option value="Opc">OPC</option>
              <option value="Opcvm">OPCVM</option>
              <option value="Fia">FIA</option>
              <option value="autres">Autres</option>
              <option value="Autres Entreprises">Autres Entreprises</option>
            </select>
          </div>

          {/* NOUVEAU BOUTON ACTUALISER - Design Bootstrap */}
          <button 
            className="btn btn-outline-primary d-flex align-items-center gap-2 shadow-sm ms-auto"
            onClick={onRefresh}
            disabled={isSearching}
            style={{ height: '38px' }}
            title="Actualiser la liste"
          >
            <FaSyncAlt className={isSearching ? "fa-spin" : ""} size={14} />
            <span className="d-none d-md-inline">Actualiser</span>
          </button>

          {/* Reset */}
          {activeFilters > 0 && (
            <button 
              className="btn btn-light border d-flex align-items-center gap-1 text-danger"
              onClick={resetFilters}
              style={{ height: '38px' }}
              title="Effacer les filtres"
            >
              <i className="fas fa-times" style={{ fontSize: '14px' }}></i>
              <span className="d-none d-md-inline">Effacer</span>
            </button>
          )}
          
          Reset
          {activeFilters > 0 && (
            <button 
              className="refresh-button d-flex align-items-center gap-1"
              onClick={resetFilters}
              style={{ 
                height: '38px', 
                padding: '0 12px',
                outline: 'none'
              }}
            >
              <FaSyncAlt size={14} />
              <span>Effacer</span>
            </button>
          )}
          
          {/* Badge filtres actifs */}
          {activeFilters > 0 && (
            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-2 py-1">
              {activeFilters}
            </span>
          )}
        </div>
      </div>

      {/* Filtres actifs sous forme de chips */}
      {activeFilters > 0 && (
        <div className="mt-2 pt-2 border-top">
          <div className="d-flex align-items-center flex-wrap gap-1">
            <small className="text-muted me-2">Filtres :</small>
            {searchTerm && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                {searchTerm}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => {
                    setLocalSearch('');
                    setSearchTerm('');
                  }}
                  style={{ 
                    color: '#6c757d', 
                    fontSize: '0.7rem',
                    outline: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                </button>
              </span>
            )}
            {accountTypeFilter && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                {accountTypeFilter}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => handleAccountTypeFilter('')}
                  style={{ 
                    color: '#6c757d', 
                    fontSize: '0.7rem',
                    outline: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                </button>
              </span>
            )}
            {statusFilter && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                {statusFilter === '0' ? 'Brouillon' : 
                 statusFilter === '1' ? 'En attente' : 
                 statusFilter === '2' ? 'Approuvé' : 'Rejeté'}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => handleStatusFilter('')}
                  style={{ 
                    color: '#6c757d', 
                    fontSize: '0.7rem',
                    outline: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                </button>
              </span>
            )}
            {isManagedAccountFilter && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                Administré: {isManagedAccountFilter === 'true' ? 'Oui' : 'Non'}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => handleManagedAccountFilter('')}
                  style={{ 
                    color: '#6c757d', 
                    fontSize: '0.7rem',
                    outline: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                </button>
              </span>
            )}
            {cashFilter && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                Cash: {cashFilter === 'asc' ? '▲' : '▼'}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={handleCashFilter}
                  style={{ 
                    color: '#6c757d', 
                    fontSize: '0.7rem',
                    outline: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                </button>
              </span>
            )}
            {aucFilter && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                AUC: {aucFilter === 'asc' ? '▲' : '▼'}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={handleAucFilter}
                  style={{ 
                    color: '#6c757d', 
                    fontSize: '0.7rem',
                    outline: 'none'
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchAndFilters;